/**
 * SAP charge objects — the five S/4HANA standard object types that can serve as
 * charge objects, the Tesseract "charge object type" model that maps to them,
 * and the per-type connector config for the live pull (wired in a later pass).
 *
 * UI-free / network-free → reusable by the admin page + the import service.
 */

export type SapObjectType =
  | 'wbs_element'
  | 'network_activity'
  | 'internal_order'
  | 'cost_center'
  | 'production_order'

export interface SapObjectTypeMeta {
  id: SapObjectType
  /** Tesseract-facing label. */
  label: string
  /** SAP-native name. */
  sapName: string
  /** The SAP key field's human label (WBS element / Order number / …). */
  keyLabel: string
}

export const SAP_OBJECT_TYPES: SapObjectTypeMeta[] = [
  { id: 'wbs_element', label: 'WBS Element', sapName: 'PS WBS Element', keyLabel: 'WBS element' },
  { id: 'network_activity', label: 'Network Activity', sapName: 'PS Network Activity', keyLabel: 'Network / activity' },
  { id: 'internal_order', label: 'Internal Order', sapName: 'CO Internal Order', keyLabel: 'Order number' },
  { id: 'cost_center', label: 'Cost Center', sapName: 'CO Cost Center', keyLabel: 'Cost center' },
  { id: 'production_order', label: 'Production Order', sapName: 'PP Production Order', keyLabel: 'Order number' },
]

export function sapObjectTypeMeta(t: SapObjectType): SapObjectTypeMeta {
  return SAP_OBJECT_TYPES.find((x) => x.id === t) ?? SAP_OBJECT_TYPES[0]!
}

export function isSapObjectType(s: string): s is SapObjectType {
  return SAP_OBJECT_TYPES.some((x) => x.id === s)
}

/** Tesseract-configured charge-object type, mapped to one or more SAP object types. */
export interface ChargeObjectType {
  id: string
  orgId: string
  name: string
  code: string | null
  /** The SAP standard object types this Tesseract type spans (one or more). */
  sapObjectTypes: SapObjectType[]
  description: string | null
  active: boolean
  displayOrder: number
}

/** A cached SAP object usable as a charge object. */
export interface SapChargeObject {
  id: string
  orgId: string
  sapObjectType: SapObjectType
  objectKey: string
  description: string | null
  controllingArea: string | null
  companyCode: string | null
  costCenter: string | null
  responsible: string | null
  status: string | null
  attributes: Record<string, unknown> | null
  chargeObjectTypeId: string | null
  active: boolean
  importedAt: string
  source: 'sap_import' | 'manual'
  /** Why the object is not selectable for new entries (null = active). */
  inactiveReason?: InactiveReason
}

// ── Live pull connector config (wire live later) ─────────────────────────────
// Each SAP object type maps to an S/4 OData entity reachable through the app's
// /api/s4 proxy, plus a field map (object key + description). Left null until
// the customer's read service is confirmed; until then the admin curates
// objects manually and the Pull action reports "connector not configured".

export interface SapObjectConnector {
  /** OData path under /api/s4 (V4). e.g. 'odata/v4/sap/opu/odata4/sap/.../Entity'. */
  odataPath: string
  /** Field in the OData row that holds the object key. */
  keyField: string
  /** Field used when `keyField` is blank on a row (e.g. external vs internal id). */
  fallbackKeyField?: string
  /** Field in the OData row that holds the description (optional). */
  descriptionField?: string
}

/** Per-type live read connectors. A type without an entry is "not connected"
 *  (the admin curates manually until its read service is wired).
 *
 *  wbs_element is live against ZUI_M12_WBS_STRUCT_O4 (CDS ZC_M12_WBS_STRUCT over
 *  PRPS) and production_order against ZUI_M12_PRDORD_O4 (CDS ZC_M12_PRDORD over
 *  AUFK/AFKO with JEST-derived status), both reached through the app's /api/s4
 *  proxy. The remaining three types are wired the same way as their read
 *  services are published. */
export const SAP_OBJECT_CONNECTORS: Partial<Record<SapObjectType, SapObjectConnector>> = {
  wbs_element: {
    odataPath: 'odata/v4/sap/opu/odata4/sap/zui_m12_wbs_struct_o4/srvd/sap/z_m12_wbs_struct_sd/0001/WbsStructure',
    // Use the coding-mask-formatted external WBS id (PRPS-POSID_EDIT, exposed as
    // WbsElementExternal) as the object key so the catalog shows "E-1234.01"
    // instead of the internal "00000000000000000005TTO". Falls back to the raw
    // internal posid (de-zeroed) when the external form is blank.
    keyField: 'WbsElementExternal',
    fallbackKeyField: 'WbsElement',
    descriptionField: 'Description',
  },
  production_order: {
    odataPath: 'odata/v4/sap/opu/odata4/sap/zui_m12_prdord_o4/srvd/sap/z_m12_prdord_sd/0001/ProductionOrder',
    // Use the de-zeroed order number (AUFK-AUFNR, exposed as ProductionOrderExternal)
    // as the object key so the catalog shows "1000000" instead of the internal
    // "000001000000". Falls back to the raw padded order number (de-zeroed) when
    // the external form is blank. Description coalesces order text -> produced
    // material so a charge line always has a meaningful label.
    keyField: 'ProductionOrderExternal',
    fallbackKeyField: 'ProductionOrder',
    descriptionField: 'Description',
  },
}

/**
 * Normalize a raw SAP WBS key to its human-readable form. The internal posid is
 * stored zero-padded ("00000000000000000005TTO"); strip the leading zeros so a
 * legacy import (or a row whose POSID_EDIT was blank) still reads "5TTO". A key
 * that already carries a coding mask (dot / dash / space) is returned as-is.
 */
export function normalizeWbsKey(raw: string): string {
  const s = (raw ?? '').trim()
  if (!s) return s
  if (/[.\-/\s]/.test(s)) return s // already coding-mask formatted
  const stripped = s.replace(/^0+/, '')
  return stripped || s
}

/** Build the synthetic primary key for a cached object. */
export function sapChargeObjectId(type: SapObjectType, objectKey: string): string {
  return `${type}:${objectKey}`
}

// ── Natural-language-driven pull ─────────────────────────────────────────────
// The admin describes what to pull in plain English; the AI provider translates
// it into a structured filter plan against the object type's field vocabulary,
// which buildODataFilter turns into an OData $filter for the proxy pull.

/** Standard S/4 OData fields per object type, used as the AI's field vocabulary
 *  and to validate the plan. (Aligned to common S/4 OData names; the connector
 *  maps these to its entity when the live read service is wired.) */
export const SAP_OBJECT_FIELDS: Record<SapObjectType, string[]> = {
  wbs_element: ['WbsElement', 'WbsElementExternal', 'ProjectID', 'ParentWbsElement', 'Description', 'WbsLevel', 'CompanyCode', 'IsBillingElement', 'IsPlanningElement', 'IsAccountAssignment', 'ProjectType', 'ProjectName'],
  network_activity: ['Network', 'NetworkActivity', 'NetworkActivityName', 'WorkCenter', 'Plant', 'ActivityControlKey'],
  internal_order: ['InternalOrder', 'InternalOrderName', 'CompanyCode', 'ControllingArea', 'ResponsibleCostCenter', 'OrderStatus', 'OrderType'],
  cost_center: ['CostCenter', 'CostCenterName', 'CompanyCode', 'ControllingArea', 'CostCenterCategory', 'ResponsiblePerson', 'IsBlocked'],
  production_order: ['ProductionOrder', 'ProductionOrderExternal', 'ProductionOrderText', 'Description', 'Material', 'Plant', 'OrderType', 'CompanyCode', 'ResponsibleCostCenter', 'IsReleased', 'IsTechnicallyComplete', 'IsClosed', 'IsDeletionFlagged', 'OrderStatus'],
}

/** Field metadata used to describe the schema to the NL translator. Carrying the
 *  SAP source column (e.g. PRPS-STUFE) + type + meaning lets the model map a
 *  request phrased in raw SAP terms ("STUFE > 3", "account assignment flag",
 *  "BELKZ") onto the OData field name + the right indicator value ('X'/''). */
export interface SapFieldMeta {
  /** OData field name (what the importer stores in attributes + filters on). */
  field: string
  /** SAP source column, e.g. 'PRPS-STUFE'. */
  sap: string
  type: 'string' | 'number' | 'flag'
  /** Plain-English meaning + synonyms the model can match against. */
  desc: string
}

export const SAP_OBJECT_FIELD_META: Partial<Record<SapObjectType, SapFieldMeta[]>> = {
  wbs_element: [
    { field: 'WbsElement', sap: 'PRPS-POSID', type: 'string', desc: 'WBS element id (internal/coded form)' },
    { field: 'WbsElementExternal', sap: 'PRPS-POSID_EDIT', type: 'string', desc: 'WBS element id, coding-mask / external form (e.g. E-1234.01)' },
    { field: 'ProjectID', sap: 'PROJ-PSPID', type: 'string', desc: 'project definition id' },
    { field: 'ParentWbsElement', sap: 'PRPS-POSID (parent)', type: 'string', desc: 'parent WBS element id' },
    { field: 'Description', sap: 'PRPS-POST1', type: 'string', desc: 'WBS element description text' },
    { field: 'WbsLevel', sap: 'PRPS-STUFE', type: 'number', desc: 'WBS hierarchy level (STUFE), numeric; "level 4 and above" => WbsLevel ge 4' },
    { field: 'CompanyCode', sap: 'PRPS-PBUKR', type: 'string', desc: 'company code' },
    { field: 'IsBillingElement', sap: 'PRPS-FAKKZ', type: 'flag', desc: 'billing element indicator (FAKKZ); boolean true/false' },
    { field: 'IsPlanningElement', sap: 'PRPS-PLAKZ', type: 'flag', desc: 'planning element indicator (PLAKZ); boolean true/false' },
    { field: 'IsAccountAssignment', sap: 'PRPS-BELKZ', type: 'flag', desc: 'account assignment element indicator (BELKZ); "account assigned" => boolean true' },
    { field: 'ProjectType', sap: 'PRPS-PRART', type: 'string', desc: 'project type' },
    { field: 'ProjectName', sap: 'PROJ-POST1', type: 'string', desc: 'project description text' },
  ],
  production_order: [
    { field: 'ProductionOrder', sap: 'AUFK-AUFNR', type: 'string', desc: 'production order number, internal zero-padded form (e.g. 000001000000)' },
    { field: 'ProductionOrderExternal', sap: 'AUFK-AUFNR', type: 'string', desc: 'production order number, external form without leading zeros (e.g. 1000000)' },
    { field: 'ProductionOrderText', sap: 'AUFK-KTEXT', type: 'string', desc: 'production order short text (often blank in this system)' },
    { field: 'Description', sap: 'AUFK-KTEXT / AFKO-PLNBEZ', type: 'string', desc: 'display label: order short text, or the produced material when the text is blank' },
    { field: 'Material', sap: 'AFKO-PLNBEZ', type: 'string', desc: 'material being produced (planned/header material number)' },
    { field: 'Plant', sap: 'AUFK-WERKS', type: 'string', desc: 'plant of the production order (e.g. 1710)' },
    { field: 'OrderType', sap: 'AUFK-AUART', type: 'string', desc: 'order type (e.g. YBM1, PP01)' },
    { field: 'CompanyCode', sap: 'AUFK-BUKRS', type: 'string', desc: 'company code' },
    { field: 'ResponsibleCostCenter', sap: 'AUFK-KOSTV', type: 'string', desc: 'responsible cost center' },
    { field: 'IsReleased', sap: 'system status REL (JEST I0002)', type: 'flag', desc: 'order is released (REL); "released"/"open for charging" true => "X", false => ""' },
    { field: 'IsTechnicallyComplete', sap: 'system status TECO (JEST I0045)', type: 'flag', desc: 'order is technically complete (TECO); true => "X", false => ""' },
    { field: 'IsClosed', sap: 'system status CLSD (JEST I0046)', type: 'flag', desc: 'order is closed (CLSD); true => "X", false => ""' },
    { field: 'IsDeletionFlagged', sap: 'system status DLFL (JEST I0076)', type: 'flag', desc: 'order has a deletion flag set; true => "X", false => ""' },
    { field: 'OrderStatus', sap: 'derived from JEST', type: 'string', desc: 'overall status code, one of: CRTD (created), REL (released), TECO (technically complete), CLSD (closed)' },
  ],
}

/**
 * Build the field-vocabulary lines for the NL translator. When `availableFields`
 * is supplied (the attribute keys actually present on imported objects) the list
 * is scoped to those — i.e. it reflects the real, imported SAP schema — falling
 * back to the static field list. Known fields get their SAP-source description.
 */
export function describeSapFields(type: SapObjectType, availableFields?: string[]): string[] {
  const meta = SAP_OBJECT_FIELD_META[type] ?? []
  const metaByField = new Map(meta.map((m) => [m.field, m]))
  const base = meta.length ? meta.map((m) => m.field) : SAP_OBJECT_FIELDS[type]
  const fields = availableFields && availableFields.length ? availableFields : base
  return fields.map((f) => {
    const m = metaByField.get(f)
    return m ? `${m.field} (SAP ${m.sap}, ${m.type}) — ${m.desc}` : `${f} (string)`
  })
}

/** The set of field names a plan may legally reference for a type, optionally
 *  extended with the real imported attribute keys. */
export function allowedFieldsFor(type: SapObjectType, extra?: string[]): Set<string> {
  const meta = (SAP_OBJECT_FIELD_META[type] ?? []).map((m) => m.field)
  return new Set<string>([...SAP_OBJECT_FIELDS[type], ...meta, ...(extra ?? [])])
}

export type SapFilterOperator = 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le' | 'contains' | 'startswith' | 'matches'

export interface SapPullFilter {
  field: string
  op: SapFilterOperator
  /** string | number | boolean — flag columns (BELKZ/FAKKZ ...) compare as boolean. */
  value: string | number | boolean
}

/** A structured query plan produced from the natural-language criteria. */
export interface SapPullPlan {
  objectType: SapObjectType
  filters: SapPullFilter[]
  /** Optional free-text matched against the description when no field fits. */
  freeText?: string
  /** Max rows to import (server-side $top). */
  top?: number
  /** One-line human summary of what will be pulled (shown for confirmation). */
  summary: string
}

function odataLiteral(v: string | number | boolean): string {
  if (typeof v === 'boolean') return String(v) // Edm.Boolean: true / false (unquoted)
  if (typeof v === 'number') return String(v)
  // SAP indicator shorthand -> OData boolean, since flag columns are exposed as
  // Edm.Boolean (a quoted 'X' would be a type-mismatch on the server $filter).
  const s = String(v)
  if (s === 'X' || s === 'x') return 'true'
  return `'${s.replace(/'/g, "''")}'`
}

/** Turn a plan's field filters into an OData V4 $filter string ('' if none).
 *  'matches' (regex) has no OData V4 equivalent, so it is omitted here and
 *  applied client-side instead (policy eligibility + pull post-filter). */
export function buildODataFilter(plan: SapPullPlan): string {
  const parts = plan.filters
    .filter((f) => f.op !== 'matches')
    .map((f) => {
      if (f.op === 'contains' || f.op === 'startswith') {
        return `${f.op}(${f.field},${odataLiteral(f.value)})`
      }
      return `${f.field} ${f.op} ${odataLiteral(f.value)}`
    })
  return parts.join(' and ')
}

/** Parse the AI's JSON response into a validated plan (drops unknown fields).
 *  `extraAllowedFields` lets the caller widen the allow-list with the real
 *  imported attribute keys so a valid filter on a field that isn't in the static
 *  list (but IS in the data) survives validation. */
export function parseSapPullPlan(text: string, objectType: SapObjectType, extraAllowedFields?: string[]): SapPullPlan | null {
  if (!text) return null
  let raw: unknown
  try { raw = JSON.parse(text) } catch {
    const m = text.match(/\{[\s\S]*\}/)
    if (!m) return null
    try { raw = JSON.parse(m[0]) } catch { return null }
  }
  const o = raw as { filters?: unknown; freeText?: unknown; top?: unknown; summary?: unknown }
  const allowed = allowedFieldsFor(objectType, extraAllowedFields)
  const ops = new Set<SapFilterOperator>(['eq', 'ne', 'gt', 'ge', 'lt', 'le', 'contains', 'startswith', 'matches'])
  const filters: SapPullFilter[] = Array.isArray(o.filters)
    ? (o.filters as Array<Record<string, unknown>>)
        .filter((f) => typeof f.field === 'string' && allowed.has(f.field) && ops.has(f.op as SapFilterOperator) && (typeof f.value === 'string' || typeof f.value === 'number' || typeof f.value === 'boolean'))
        .map((f) => ({ field: f.field as string, op: f.op as SapFilterOperator, value: f.value as string | number | boolean }))
    : []
  return {
    objectType,
    filters,
    freeText: typeof o.freeText === 'string' && o.freeText.trim() ? o.freeText.trim() : undefined,
    // No default cap — the pull paginates through ALL matches. Only honor an
    // explicit limit the model set because the request named one.
    top: typeof o.top === 'number' && o.top > 0 ? o.top : undefined,
    summary: typeof o.summary === 'string' && o.summary.trim() ? o.summary.trim() : 'Pull matching objects',
  }
}

// ── Charge-object policies (NL-defined eligibility rules) ─────────────────────
// An admin describes, in plain English, which synced SAP objects are valid to
// charge ("WBS elements that are account-assignment elements above level 4").
// The same NL→filter planning that drives the pull turns it into a structured
// predicate over the object's SAP fields. The predicate is evaluated CLIENT-SIDE
// against each cached object's attributes to decide whether it is selectable.

export type PolicyEffect =
  /** Objects matching ANY enabled 'eligible' policy are chargeable; if at least
   *  one 'eligible' policy exists, non-matching objects become ineligible. */
  | 'eligible'
  /** Objects matching an 'ineligible' policy are never chargeable (wins over eligible). */
  | 'ineligible'

export interface ChargeObjectPolicy {
  id: string
  orgId: string
  name: string
  sapObjectType: SapObjectType
  /** The original plain-English text the admin typed. */
  nlText: string
  /** The structured predicate (AND of these filters) the policy matches on. */
  predicate: SapPullFilter[]
  effect: PolicyEffect
  enabled: boolean
  createdAt: string
}

/** Why an object is not selectable. null = active/selectable. */
export type InactiveReason = 'not_synced' | 'policy' | 'manual' | null

function asNumber(v: unknown): number {
  if (typeof v === 'number') return v
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'string') { const n = Number.parseFloat(v); return Number.isFinite(n) ? n : NaN }
  return NaN
}

/** Coerce a SAP indicator to a boolean. The OData layer exposes flag columns
 *  (BELKZ/FAKKZ/PLAKZ ...) as JSON booleans, but a policy/plan may carry the SAP
 *  shorthand 'X' / '' (or true/false). Treat them equivalently. */
function asFlag(v: unknown): boolean {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v !== 0
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase()
    return s === 'x' || s === 'true' || s === 'yes' || s === '1'
  }
  return false
}

/** True when this eq/ne should be compared as a SAP flag rather than as text:
 *  either operand is a real boolean, or the filter value is the 'X'/'' shorthand. */
function isFlagComparison(a: unknown, b: string | number | boolean): boolean {
  if (typeof a === 'boolean' || typeof b === 'boolean') return true
  if (typeof b === 'string') { const s = b.trim().toLowerCase(); return s === 'x' || s === '' }
  return false
}

function looseEq(a: unknown, b: string | number | boolean): boolean {
  // Flag-aware: an OData boolean attribute vs an 'X'/''/true filter value.
  if (isFlagComparison(a, b)) return asFlag(a) === asFlag(b)
  if (typeof b === 'number') return asNumber(a) === b
  return String(a ?? '').trim().toLowerCase() === String(b).trim().toLowerCase()
}

/** Evaluate a single filter against an object's attribute bag. */
export function evaluateFilter(attributes: Record<string, unknown> | null | undefined, f: SapPullFilter): boolean {
  const v = attributes?.[f.field]
  switch (f.op) {
    case 'eq': return looseEq(v, f.value)
    case 'ne': return !looseEq(v, f.value)
    case 'gt': return asNumber(v) > asNumber(f.value)
    case 'ge': return asNumber(v) >= asNumber(f.value)
    case 'lt': return asNumber(v) < asNumber(f.value)
    case 'le': return asNumber(v) <= asNumber(f.value)
    case 'contains': return String(v ?? '').toLowerCase().includes(String(f.value).toLowerCase())
    case 'startswith': return String(v ?? '').toLowerCase().startsWith(String(f.value).toLowerCase())
    // Regex match — for format/pattern rules a single value can't express, e.g.
    // "starts with a letter then a dash" => '^[A-Za-z]-'. Case-insensitive,
    // and a malformed pattern fails closed (no match) rather than throwing.
    case 'matches': {
      try { return new RegExp(String(f.value), 'i').test(String(v ?? '')) } catch { return false }
    }
    default: return false
  }
}

/** SAP key fields: stored upper case, so a case-insensitive prefix or equality
 *  test can be sent to SAP upper-cased without losing a row. */
const UPPERCASE_KEY_FIELDS = new Set([
  'WbsElement', 'WbsElementExternal', 'ProjectID', 'ParentWbsElement',
  'ProductionOrder', 'ProductionOrderExternal', 'CompanyCode', 'Plant', 'OrderType', 'ProjectType',
])

const odataString = (v: string) => `'${v.replace(/'/g, "''")}'`

/**
 * The part of the eligibility policies SAP can apply itself, as an OData
 * `$filter`, so a live read returns only what can be charged instead of every
 * object in the system. Eligible policies are ANDed filter by filter (see
 * computeEligibility), so each translatable filter can be sent on its own.
 *
 * Only filters that cannot drop an eligible row are sent: numeric comparisons,
 * flags, and equality / prefix on upper-case SAP keys. Everything else (regex,
 * free-text contains, ineligible carve-outs) stays with computeEligibility on
 * the rows that come back. Returns '' when nothing translates.
 */
export function eligibilityODataFilter(type: SapObjectType, policies: ChargeObjectPolicy[]): string {
  const meta = new Map((SAP_OBJECT_FIELD_META[type] ?? []).map((m) => [m.field, m.type]))
  const clauses: string[] = []
  for (const p of policies) {
    if (!p.enabled || p.effect !== 'eligible' || !p.predicate?.length) continue
    for (const f of p.predicate) {
      const kind = meta.get(f.field)
      if (!kind || !/^[A-Za-z][A-Za-z0-9_]*$/.test(f.field)) continue
      const raw = f.value
      // Flags only where the service publishes a real boolean (the WBS
      // structure); the order service carries 'X' / '' strings.
      if (kind === 'flag' && type === 'wbs_element' && (f.op === 'eq' || f.op === 'ne')) {
        const on = raw === true || String(raw).toUpperCase() === 'X' || String(raw).toLowerCase() === 'true'
        clauses.push(`${f.field} ${f.op} ${on ? 'true' : 'false'}`)
      } else if (kind === 'number' && ['eq', 'ne', 'gt', 'ge', 'lt', 'le'].includes(f.op)) {
        const n = Number(raw)
        if (Number.isFinite(n)) clauses.push(`${f.field} ${f.op} ${n}`)
      } else if (kind === 'string' && UPPERCASE_KEY_FIELDS.has(f.field) && String(raw) !== '') {
        const v = odataString(String(raw).toUpperCase())
        if (f.op === 'eq' || f.op === 'ne') clauses.push(`${f.field} ${f.op} ${v}`)
        else if (f.op === 'startswith') clauses.push(`startswith(${f.field},${v})`)
      }
    }
  }
  return clauses.join(' and ')
}

/** A policy matches an object when ALL of its filters match (logical AND). An
 *  empty predicate never matches (a no-op policy, so it can't silently gate all). */
export function policyMatches(policy: ChargeObjectPolicy, attributes: Record<string, unknown> | null | undefined): boolean {
  if (!policy.predicate || policy.predicate.length === 0) return false
  return policy.predicate.every((f) => evaluateFilter(attributes, f))
}

export interface EligibilityResult {
  /** Whether the object is selectable for NEW time entries. */
  active: boolean
  reason: InactiveReason
}

/**
 * Decide whether a synced object is eligible to charge, given the enabled
 * policies for its type. Semantics (eligible policies are stacked filters that
 * NARROW, so each one you add tightens the chargeable set):
 *   - an 'ineligible' match always wins → not selectable;
 *   - if any 'eligible' policy exists, the object must match EVERY one (AND);
 *   - with no 'eligible' policy, everything is eligible by default.
 * (To express an OR / union across conditions, put them in one policy's
 * predicate via the field operators, or use an 'ineligible' policy to carve out
 * exceptions.)
 */
export function computeEligibility(
  attributes: Record<string, unknown> | null | undefined,
  policies: ChargeObjectPolicy[],
): EligibilityResult {
  const enabled = policies.filter((p) => p.enabled)
  const ineligible = enabled.filter((p) => p.effect === 'ineligible')
  if (ineligible.some((p) => policyMatches(p, attributes))) return { active: false, reason: 'policy' }
  const eligible = enabled.filter((p) => p.effect === 'eligible')
  if (eligible.length === 0) return { active: true, reason: null }
  return eligible.every((p) => policyMatches(p, attributes))
    ? { active: true, reason: null }
    : { active: false, reason: 'policy' }
}

/** Human-readable predicate, e.g. "IsAccountAssignment = X and WbsLevel > 4". */
export function summarizePredicate(filters: SapPullFilter[]): string {
  if (!filters.length) return '(no conditions)'
  const opWord: Record<SapFilterOperator, string> = {
    eq: '=', ne: '≠', gt: '>', ge: '≥', lt: '<', le: '≤', contains: 'contains', startswith: 'starts with', matches: 'matches',
  }
  return filters.map((f) => `${f.field} ${opWord[f.op]} ${f.value}`).join(' and ')
}

// ── SAP object → timesheet charge code ───────────────────────────────────────
// Synced SAP charge objects surface in the timesheet's charge-line catalog so
// workers can charge against them. The synthetic id (sapChargeObjectId) is what
// the time_entry stores; getChargeCode resolves it back even after the object
// goes inactive, so historical entries still render.

import type { TsChargeCode } from '../types/timesheet.types'

/** Synthetic contract id carried by charge codes sourced from SAP objects. */
export function sapChargeContractId(type: SapObjectType): string {
  return `sap:${type}`
}

/** Map a cached SAP charge object to the timesheet's TsChargeCode shape. */
export function sapChargeObjectToChargeCode(obj: SapChargeObject): TsChargeCode {
  return {
    id: obj.id,
    contractId: sapChargeContractId(obj.sapObjectType),
    wbs: obj.objectKey,
    description: obj.description ?? sapObjectTypeMeta(obj.sapObjectType).label,
    laborCategories: [],
    allowable: obj.active,
    pool: null,
    costCenter: obj.costCenter,
    // billable is unknown for a bare SAP object; leave null so the billability
    // adapter derives the safe (not-billable) default rather than guessing.
    billable: null,
    // PRPS-PRART. Carried because direct-versus-indirect policy is written
    // against it, and without it the classification has nowhere to read from.
    projectType: (obj.attributes?.['ProjectType'] as string | undefined)?.trim() || null,
  }
}
